import { nextLessonToSchedule } from "@pwz/domain";
import { CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ScheduledLessonRecord } from "../../../api/schemas.js";
import { lessonStatusBadgeClass, lessonStatusLabel } from "../../../lib/lesson-status.js";
import { formatInTimeZone, formatTimeInZone, getDeviceTimeZone } from "../../../lib/timezone.js";
import { EmptyState } from "../../../ui/EmptyState.js";

export interface DealStudyPlanProps {
  readonly lessons: readonly ScheduledLessonRecord[];
  readonly venueNames: ReadonlyMap<string, string>;
  readonly venueTimezones: ReadonlyMap<string, string>;
  /** Название курса по id зачисления: с докупкой планов несколько, и их надо подписывать. */
  readonly courseNames?: ReadonlyMap<string, string>;
  /** Урок без времени: назначается через обычный диалог записи. */
  readonly onSchedule: (lesson: ScheduledLessonRecord) => void;
  /** Урок с временем: открывается карточка урока со всеми действиями. */
  readonly onOpen: (lesson: ScheduledLessonRecord) => void;
}

function when(lesson: ScheduledLessonRecord, zone: string): string {
  if (!lesson.startTime) return "не назначен";
  const date = formatInTimeZone(lesson.startTime, zone, { day: "numeric", month: "short" });
  return `${date} ${formatTimeInZone(lesson.startTime, zone)}`;
}

/**
 * Учебный план сделки: все уроки зачисления по порядку — что уже стоит в расписании, что
 * ещё нет. Отсюда менеджер планирует следующий урок и открывает карточку существующего,
 * не выискивая его в сетке.
 */
export function DealStudyPlan(props: DealStudyPlanProps) {
  const deviceZone = getDeviceTimeZone();
  const lessons = [...props.lessons].sort((a, b) => a.lessonOrder - b.lessonOrder);

  if (lessons.length === 0) {
    return (
      <EmptyState
        title="Учебного плана нет"
        description="Создайте зачисление — уроки появятся по плану курса."
        testId="deal-plan-empty"
      />
    );
  }

  // Несколько зачислений (докупка) — планы идут секциями со своим заголовком-курсом,
  // одно — плоским списком, как раньше.
  const groups = new Map<string, ScheduledLessonRecord[]>();
  for (const lesson of lessons) {
    const list = groups.get(lesson.enrollmentId);
    if (list) list.push(lesson);
    else groups.set(lesson.enrollmentId, [lesson]);
  }
  if (groups.size > 1) {
    return (
      <div className="space-y-4" data-testid="deal-study-plan">
        {[...groups.entries()].map(([enrollmentId, groupLessons]) => (
          <section key={enrollmentId} data-testid={`deal-plan-course-${enrollmentId}`}>
            <h3 className="pwz-eyebrow mb-2">{props.courseNames?.get(enrollmentId) ?? "Курс"}</h3>
            <LessonList {...props} lessons={groupLessons} deviceZone={deviceZone} />
          </section>
        ))}
      </div>
    );
  }

  return (
    <LessonList {...props} lessons={lessons} deviceZone={deviceZone} testId="deal-study-plan" />
  );
}

function LessonList(
  props: DealStudyPlanProps & { readonly deviceZone: string; readonly testId?: string },
) {
  const deviceZone = props.deviceZone;
  // Уроки курса идут строго по порядку: записать можно только следующий — первый без времени.
  // Остальные ждут своей очереди, и кнопки у них нет: раньше «Запланировать» стояла у каждого,
  // и третий урок оказывался в сетке раньше первого.
  const next = nextLessonToSchedule(
    props.lessons.map((lesson) => ({
      lessonId: lesson.id,
      lessonOrder: lesson.lessonOrder,
      status: lesson.status,
      startTime: lesson.startTime ?? null,
      durationMinutes: lesson.durationMinutes,
    })),
  );
  return (
    <ol className="space-y-2" {...(props.testId ? { "data-testid": props.testId } : {})}>
      {props.lessons.map((lesson) => {
        const zone =
          (lesson.venueId ? props.venueTimezones.get(lesson.venueId) : undefined) ?? deviceZone;
        const scheduled = Boolean(lesson.startTime);
        return (
          <li
            key={lesson.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-pad-200 bg-white px-3 py-2.5"
            data-testid={`deal-plan-lesson-${lesson.id}`}
          >
            <div className="min-w-0">
              <p className="text-ink-900 text-sm">
                <span className="pwz-readout mr-1.5 text-ink-600">{lesson.lessonOrder}.</span>
                <span className="pwz-readout font-semibold">{when(lesson, zone)}</span>
                {lesson.venueId ? (
                  <span className="text-ink-600">
                    {" · "}
                    {props.venueNames.get(lesson.venueId) ?? "площадка"}
                  </span>
                ) : null}
              </p>
              {/*
                Урок плана рождается в статусе `scheduled`, но до выбора времени он никуда не
                записан: подпись «Записан» рядом с «не назначен» читалась как поломка.
              */}
              <span
                className={`mt-1 inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${
                  scheduled ? lessonStatusBadgeClass(lesson.status) : "bg-pad-200 text-ink-600"
                }`}
              >
                {scheduled
                  ? lessonStatusLabel(lesson.status)
                  : next?.lessonId === lesson.id
                    ? "Следующий"
                    : "В очереди"}
              </span>
            </div>
            {scheduled ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid={`deal-plan-open-${lesson.id}`}
                onClick={() => props.onOpen(lesson)}
              >
                Открыть
              </Button>
            ) : next?.lessonId === lesson.id ? (
              <Button
                type="button"
                size="sm"
                data-testid={`deal-plan-schedule-${lesson.id}`}
                onClick={() => props.onSchedule(lesson)}
              >
                <CalendarPlus className="size-4" />
                Запланировать
              </Button>
            ) : (
              <span className="text-ink-500 text-xs" data-testid={`deal-plan-waiting-${lesson.id}`}>
                после урока {lesson.lessonOrder - 1}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
